import {
  Circle,
  GoogleMap,
  Marker,
  Polyline,
} from "@react-google-maps/api";
import { Grid, Typography, Switch } from "@mui/material"

import { AlertTypes } from "../../types";
import { markerConstants, markerRadiusColorConstants } from "../../constants/marker.constants";
import { useEffect, useRef } from "react";

const mapContainerStyle = {
  width: '100%',
  height: '400px'
};

type AlertMapComponentProps = {
  showAllLocations: boolean,
  setShowAllLocations: React.Dispatch<React.SetStateAction<boolean>>,
  location: AlertTypes.AlertsLocations | undefined,
  alert: AlertTypes.Model,
}

// AlertPathMapComponent optionally shows a device alert path on the map
export const AlertPathMapComponent = ({ showAllLocations, setShowAllLocations, location, alert }: AlertMapComponentProps) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const isMountedRef = useRef<boolean>(false);

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <Grid item md={12} sm={12} xs={12}>
      <Typography mb={2} fontWeight={400}>
        {showAllLocations ? 'Alert Path' : 'Last Location'}
      </Typography>

      <Switch
        checked={showAllLocations}
        onChange={(e) => setShowAllLocations(e.target.checked)}
        name="showAllLocationsToggle"
        color="primary"
      />
      <GoogleMap
          onLoad={handleMapLoad}
          mapContainerStyle={mapContainerStyle}
          zoom={15}
          center={{
            lat: Number(location?.latitude),
            lng: Number(location?.longitude)
          }}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            fullscreenControl: true,
            mapTypeControl: true,
            streetViewControl: true,
            scaleControl: true,
            rotateControl: true,
            clickableIcons: false,
            scrollwheel: true,
            draggable: true,
            draggableCursor: 'default',
            draggingCursor: 'pointer',
            keyboardShortcuts: true,
            disableDoubleClickZoom: false,
            noClear: false,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [
                  {
                    visibility: 'off',
                  },
                ],
              },
            ],
          }}
        >
          {showAllLocations ? (
            <>
              {/* // put the first location on the map */}
              {alert?.locations && alert?.locations[0] && (
                <Marker
                  position={{
                    lat: Number(alert?.locations[0]?.latitude),
                    lng: Number(alert?.locations[0]?.longitude),
                  }}
                  options={{
                    icon: {
                      url: markerConstants['alert'],
                      scaledSize: new google.maps.Size(10, 10),
                      scale: 1,
                      origin: new google.maps.Point(0, 0),
                    }
                  }}
                />
              )}
              <PolylineGradient locations={alert?.locations} />
            </>
          ) : (
            <Marker
              position={{
                lat: Number(location?.latitude),
                lng: Number(location?.longitude),
              }}
              options={{
                icon: {
                  url: markerConstants['alert'],
                  scaledSize: new google.maps.Size(10, 10),
                  scale: 1,
                  origin: new google.maps.Point(0, 0),
                }
              }}
            />
          )}
          {!location?.isPrecise &&
            <Circle
              center={{
                lat: Number(location?.latitude),
                lng: Number(location?.longitude),
              }}
              radius={location?.appxDistMeters}
              options={{
                strokeColor: markerRadiusColorConstants['alert'],
                strokeWeight: 0.1,
                fillOpacity: 0.2,
                fillColor: markerRadiusColorConstants['alert'],
                center: {
                  lat: Number(location?.latitude),
                  lng: Number(location?.longitude),
                }
              }}
            />
          }
        </GoogleMap>
    </Grid>
  );
};

type PolylineGradientProps = {
  locations: AlertTypes.AlertsLocations[]
}

// PolylineGradient draws line segments connecting locations with a colored gradient
function PolylineGradient({ locations }: PolylineGradientProps) {
  // trim locations
  locations = locations?.map(loc => ({
    ...loc,
    latitude: String(loc?.latitude).trim(),
    longitude: String(loc?.longitude).trim(),
  }));

  const pathCoordinates = locations.map((l) => ({
    lat: Number(l.latitude),
    lng: Number(l.longitude),
  }));

  return (
    <>
      <Polyline
        path={pathCoordinates}
        options={{
            strokeColor: "#ff2527",
            strokeOpacity: 0.75,
            strokeWeight: 2,
            icons: [
              {
                offset: "0",
                repeat: "20px"
              }
            ]
        }}
      />
    </>
  );

}